import React, { useState, useContext, useEffect } from 'react';
import { DarkButton, IconButton } from 'components/style/Global';
import { IoMdClose } from 'react-icons/io';
import { FileUploader } from 'react-drag-drop-files';
import { useTheme } from 'styled-components';
import { Modal } from 'components/NewPortal/Header/AddCampaignModal';
import { API } from 'aws-amplify';
import { getPreSignedUrl } from 'graphql/queries';
import {
  GetPreSignedUrlQuery,
  S3Method,
  StoreQueriesV2MutationVariables,
  CreativeInput,
  CreativeMetricsInput,
} from 'API';
import { v4 as uuidv4 } from 'uuid';
import { useCampaignContext } from 'context/CampaignContext';
import APIContext from 'context/APIContext';
import InputContext from 'context/InputContext';
import { useStoreQueries } from 'customHooks/useStoreQueries';
import {
  ModulContainer,
  HeadModul,
  MidRow,
  Selections,
  BottomRow,
  NavigationButton,
  RightPane,
} from 'components/Main/table/TableStyles';
import ReactLoading from 'react-loading';

interface CreativeData {
  creativeID: string; // Can be single ID or comma-separated IDs for multiselect
  creativeName: string;
  file?: File | null; // Allow null for edit mode when no new file is uploaded
  selectedMetrics: string[];
  customMetrics?: Array<{ label: string; value: number }>; // Add customMetrics for edit mode
  outputLocation?: string; // For backend storage
  localFileUrl?: string; // Local file URL for preview
  fileType?: string; // MIME type of the file for proper preview
}

interface AddCreativeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (creativeData: CreativeData) => void;
  availableCreativeIds: Array<{ id: string; label: string }>;
  availableMetrics: Array<{ id: string; label: string }>;
  sectionPosition: string; // e.g., "901"
  existingCreatives: any[]; // Existing creatives in this section
  editingCreative?: any; // The creative being edited
  isEditMode?: boolean; // Whether we're in edit mode
}

const AddCreativeModal: React.FC<AddCreativeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  availableCreativeIds,
  availableMetrics,
  sectionPosition,
  existingCreatives,
  editingCreative,
  isEditMode = false,
}) => {
  const [creativeId, setCreativeId] = useState('');
  const [selectedCreativeIds, setSelectedCreativeIds] = useState<string[]>([]);
  const [isMultiSelect, setIsMultiSelect] = useState(false);
  const [customCreativeName, setCustomCreativeName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [availableMetricsObjects, setAvailableMetricsObjects] = useState<
    Array<{
      position: number;
      label: string;
      value: number;
      column: string;
      decimals: number;
    }>
  >([]);
  const [customMetrics, setCustomMetrics] = useState<Array<{ label: string; value: number; decimals?: number }>>([]);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMetric, setEditingMetric] = useState<{
    type: 'data' | 'custom';
    index?: number;
    metricId?: string;
    label: string;
    value: string;
    decimals?: number;
  } | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editValue, setEditValue] = useState('');
  const [editDecimals, setEditDecimals] = useState('2');
  const [customLabel, setCustomLabel] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [previousCreativeId, setPreviousCreativeId] = useState<string>('');
  const theme = useTheme();

  // Get campaign context and API environment
  const { activeCampaign } = useCampaignContext();
  const { getEnvironment } = useContext(APIContext);
  const { creativeLibrarySections } = useContext(InputContext);
  const [saveQueries] = useStoreQueries();

  const fileTypes = ['JPG', 'PNG', 'MP4', 'MOV', 'AVI', 'HTML', 'HTM'];

  // Initialize form with editing creative data if in edit mode
  useEffect(() => {
    if (isEditMode && editingCreative) {
      // Check if creative ID contains comma (multiselect)
      const creativeIdValue = editingCreative.creativeID || '';
      if (creativeIdValue.includes(',')) {
        // Multi-select mode
        const ids = creativeIdValue
          .split(',')
          .map((id: string) => id.trim())
          .filter((id: string) => id !== '');
        setSelectedCreativeIds(ids);
        setIsMultiSelect(true);
        setCustomCreativeName(editingCreative.creativeName || 'Custom');
        setCreativeId(''); // Clear single select
      } else {
        // Single select mode
        setCreativeId(creativeIdValue);
        setSelectedCreativeIds([]);
        setIsMultiSelect(false);
        setCustomCreativeName('');
      }
      setPreviousCreativeId(creativeIdValue); // Set as previous to avoid triggering change

      // Extract metrics data to pre-populate the form
      const dataMetrics: string[] = [];
      const customMetricsData: Array<{ label: string; value: number; decimals?: number }> = [];
      const availableMetricsObjectsData: Array<{
        position: number;
        label: string;
        value: number;
        column: string;
        decimals: number;
      }> = [];

      // Separate data metrics from custom metrics
      if (editingCreative.metrics) {
        editingCreative.metrics.forEach((metric: any) => {
          // Check if this is a data metric (has column reference)
          if (metric.column) {
            // This is an available metric
            dataMetrics.push(metric.column);
            availableMetricsObjectsData.push({
              position: metric.position,
              label: metric.label,
              value: metric.value,
              column: metric.column,
              decimals: metric.decimals ?? 2,
            });
          } else {
            // This is a custom metric
            customMetricsData.push({
              label: metric.label,
              value: metric.value,
              decimals: metric.decimals || 2,
            });
          }
        });
      }

      setSelectedMetrics(dataMetrics);
      setAvailableMetricsObjects(availableMetricsObjectsData);
      setCustomMetrics(customMetricsData);

      // Note: File is not set since it's already uploaded to S3
      // The user can upload a new file to replace it if desired
    } else if (!isEditMode) {
      // Reset form when not in edit mode
      setCreativeId('');
      setSelectedCreativeIds([]);
      setIsMultiSelect(false);
      setCustomCreativeName('');
      setSelectedFile(null);
      setSelectedMetrics([]);
      setAvailableMetricsObjects([]);
      setCustomMetrics([]);
      setPreviousCreativeId(''); // Reset tracking
    }
  }, [isEditMode, editingCreative, availableMetrics]);

  // Track Creative ID changes and clear metrics when it changes
  useEffect(() => {
    const currentValue = isMultiSelect ? selectedCreativeIds.join(',') : creativeId;
    if (currentValue && previousCreativeId && currentValue !== previousCreativeId) {
      // Creative ID actually changed, clear metrics
      setSelectedMetrics([]);
      setCustomMetrics([]);
    }
    setPreviousCreativeId(currentValue);
  }, [creativeId, selectedCreativeIds, isMultiSelect, previousCreativeId]);

  const handleFileChange = (file: File) => {
    setSelectedFile(file);
  };

  const handleSingleSelectToggle = (selectedId: string) => {
    setCreativeId(creativeId === selectedId ? '' : selectedId);
  };

  const handleMultiSelectToggle = (selectedId: string) => {
    setSelectedCreativeIds((prev) => {
      if (prev.includes(selectedId)) {
        return prev.filter((id) => id !== selectedId);
      } else {
        return [...prev, selectedId];
      }
    });
  };

  const toggleSelectMode = () => {
    setIsMultiSelect(!isMultiSelect);
    // Clear selections when switching modes
    setCreativeId('');
    setSelectedCreativeIds([]);
    setCustomCreativeName('');
  };

  const handleMetricToggle = (metricId: string) => {
    const isCurrentlySelected = selectedMetrics.includes(metricId);

    if (isCurrentlySelected) {
      // Remove from both selectedMetrics and availableMetricsObjects
      setSelectedMetrics(selectedMetrics.filter((id) => id !== metricId));
      setAvailableMetricsObjects(availableMetricsObjects.filter((obj) => obj.column !== metricId));
    } else if (selectedMetrics.length + customMetrics.length < 4) {
      // Add to selectedMetrics
      setSelectedMetrics([...selectedMetrics, metricId]);

      // Create complete metrics object with data
      const sectionData = creativeLibrarySections.find((section) => section.position === sectionPosition)?.data || [];

      let metricValue = 0;
      let hasValidData = false;

      if (isMultiSelect && selectedCreativeIds.length > 0) {
        // For multiselect, find all matching items and sum the metric values
        const matchingDataItems = sectionData.filter((item: any) => {
          return selectedCreativeIds.some((id) => item.creative_id?.toString() === id.toString());
        });

        if (matchingDataItems.length > 0) {
          metricValue = matchingDataItems.reduce((sum: number, item: any) => {
            const value = item[metricId];
            return sum + (typeof value === 'number' ? value : 0);
          }, 0);
          hasValidData = true;
        }
      } else {
        // For single select, find single matching item
        const matchingDataItem = sectionData.find((item: any) => {
          return item.creative_id?.toString() === creativeId.toString();
        });

        if (matchingDataItem && matchingDataItem[metricId] !== undefined) {
          metricValue = matchingDataItem[metricId];
          hasValidData = true;
        }
      }

      if (hasValidData) {
        const newMetricObject = {
          position: selectedMetrics.length + 1, // Will be recalculated later
          label: metricId.charAt(0).toUpperCase() + metricId.slice(1),
          value: metricValue,
          column: metricId,
          decimals: 2,
        };

        setAvailableMetricsObjects([...availableMetricsObjects, newMetricObject]);
      }
    }
  };

  const handleAddCustomMetric = () => {
    if (customLabel && customValue && availableMetricsObjects.length + customMetrics.length < 4) {
      setCustomMetrics([
        ...customMetrics,
        {
          label: customLabel,
          value: parseFloat(customValue) || 0,
          decimals: 2, // Default decimals for custom metrics
        },
      ]);
      setCustomLabel('');
      setCustomValue('');
      setShowCustomModal(false);
    }
  };

  const handleEditDataMetric = (metricId: string) => {
    // Look for metric in availableMetricsObjects first
    const availableMetric = availableMetricsObjects.find((m) => m.column === metricId);

    if (availableMetric) {
      setEditingMetric({
        type: 'data',
        metricId,
        label: availableMetric.label,
        value: availableMetric.value.toString(),
        decimals: availableMetric.decimals,
      });
      setEditLabel(availableMetric.label);
      setEditValue(availableMetric.value.toString());
      setEditDecimals(availableMetric.decimals.toString());
      setShowEditModal(true);
    } else {
      // Fallback for legacy logic
      const metric = availableMetrics.find((m) => m.id === metricId);
      if (metric && editingCreative && editingCreative.metrics) {
        const storedMetric = editingCreative.metrics.find(
          (m: any) =>
            m.label === metric.label ||
            m.label === metric.id ||
            m.label.toLowerCase() === metric.label.toLowerCase() ||
            m.label.toLowerCase() === metric.id.toLowerCase(),
        );

        const storedValue = storedMetric ? storedMetric.value.toString() : '0';
        const storedDecimals = storedMetric?.decimals || 2;

        setEditingMetric({ type: 'data', metricId, label: metric.label, value: storedValue, decimals: storedDecimals });
        setEditLabel(metric.label);
        setEditValue(storedValue);
        setEditDecimals(storedDecimals.toString());
        setShowEditModal(true);
      }
    }
  };

  const handleEditCustomMetric = (index: number) => {
    const customMetric = customMetrics[index];
    if (customMetric) {
      setEditingMetric({
        type: 'custom',
        index,
        label: customMetric.label,
        value: customMetric.value.toString(),
        decimals: customMetric.decimals || 2,
      });
      setEditLabel(customMetric.label);
      setEditValue(customMetric.value.toString());
      setEditDecimals((customMetric.decimals || 2).toString());
      setShowEditModal(true);
    }
  };

  const handleSaveEdit = () => {
    if (!editingMetric || !editLabel || !editValue) return;

    const newDecimals = parseInt(editDecimals) || 2;

    if (editingMetric.type === 'custom' && editingMetric.index !== undefined) {
      // Update existing custom metric
      const updatedCustomMetrics = [...customMetrics];
      updatedCustomMetrics[editingMetric.index] = {
        label: editLabel,
        value: parseFloat(editValue) || 0,
        decimals: newDecimals,
      };
      setCustomMetrics(updatedCustomMetrics);
    } else if (editingMetric.type === 'data' && editingMetric.metricId) {
      // For available metrics, check what was changed:
      const originalMetric = availableMetricsObjects.find((obj) => obj.column === editingMetric.metricId);
      const originalLabel = originalMetric?.label || editingMetric.label;
      const originalValue = originalMetric?.value || parseFloat(editingMetric.value) || 0;
      const originalDecimals = originalMetric?.decimals || editingMetric.decimals || 2;
      const newValue = parseFloat(editValue) || 0;

      // If only LABEL or DECIMALS changed, keep as available metric
      if ((editLabel !== originalLabel || newDecimals !== originalDecimals) && newValue === originalValue) {
        // Update the object in availableMetricsObjects
        setAvailableMetricsObjects(
          availableMetricsObjects.map((obj) =>
            obj.column === editingMetric.metricId ? { ...obj, label: editLabel, decimals: newDecimals } : obj,
          ),
        );
      }
      // If VALUE changed, convert to custom metric
      else if (newValue !== originalValue) {
        // Remove from available metrics and add as custom metric
        setSelectedMetrics(selectedMetrics.filter((id) => id !== editingMetric.metricId));
        setAvailableMetricsObjects(availableMetricsObjects.filter((obj) => obj.column !== editingMetric.metricId));
        setCustomMetrics([
          ...customMetrics,
          {
            label: editLabel,
            value: newValue,
            decimals: newDecimals,
          },
        ]);
      }
      // If both changed, also convert to custom metric
      else if ((editLabel !== originalLabel || newDecimals !== originalDecimals) && newValue !== originalValue) {
        setSelectedMetrics(selectedMetrics.filter((id) => id !== editingMetric.metricId));
        setAvailableMetricsObjects(availableMetricsObjects.filter((obj) => obj.column !== editingMetric.metricId));
        setCustomMetrics([
          ...customMetrics,
          {
            label: editLabel,
            value: newValue,
            decimals: newDecimals,
          },
        ]);
      }
    }

    // Reset edit state
    setEditingMetric(null);
    setEditLabel('');
    setEditValue('');
    setEditDecimals('2');
    setShowEditModal(false);
  };

  const handleCancelEdit = () => {
    setEditingMetric(null);
    setEditLabel('');
    setEditValue('');
    setEditDecimals('2');
    setShowEditModal(false);
  };

  const handleRemoveCustomMetric = (index: number) => {
    setCustomMetrics(customMetrics.filter((_, i) => i !== index));
  };

  const getTotalMetricsCount = () => availableMetricsObjects.length + customMetrics.length;
  const canAddMoreMetrics = () => getTotalMetricsCount() < 4;

  // File upload to S3 using presigned URL
  const uploadFileToS3 = async (file: File): Promise<string | null> => {
    try {
      const fileN = `${uuidv4()}_${file.name}`;
      const preSignedURL = (await API.graphql({
        query: getPreSignedUrl,
        variables: {
          ClientMethod: S3Method.put_object,
          Client: {
            code: activeCampaign?.ClientCode,
            country: activeCampaign?.ClientCountry,
          },
          Environment: getEnvironment,
          FileName: fileN,
          ContentType: file.type,
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: GetPreSignedUrlQuery };

      const url = preSignedURL.data.getPreSignedUrl?.url;
      if (!url) throw new Error('Failed to get presigned URL');

      // Upload file to S3
      await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });

      return fileN; // Return the file name for storage
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    }
  };
        console.log("creativeLibrarySections:", creativeLibrarySections);
  const handleFetch = async () => {
    const finalCreativeId = isMultiSelect ? selectedCreativeIds.join(',') : creativeId;
    const finalCreativeName = isMultiSelect
      ? customCreativeName
      : availableCreativeIds.find((c) => c.id === creativeId)?.label || `Creative ${creativeId}`;

    if (!finalCreativeId || (availableMetricsObjects.length === 0 && customMetrics.length === 0)) return;

    // In edit mode, file upload is optional (user may only want to change metrics)
    if (!isEditMode && !selectedFile) return;

    setIsLoading(true);

    try {
      let outputLocation: string | null = null;
      let localFileUrl: string | null = null;
      let fileType: string | undefined = undefined;

      // Upload new file if provided (both in add and edit mode)
      if (selectedFile) {
        outputLocation = await uploadFileToS3(selectedFile);

        if (!outputLocation) {
          throw new Error('Failed to upload file');
        }

        // Create local file URL for immediate preview
        localFileUrl = URL.createObjectURL(selectedFile);
        fileType = selectedFile.type;
      } else if (isEditMode && editingCreative) {
        // In edit mode without new file, keep existing file data
        outputLocation = editingCreative.file;
        fileType = editingCreative.fileType;
      }

      // Get creative name from the selected creative ID
      // const selectedCreative = availableCreativeIds.find((c) => c.id === creativeId);
      // const creativeName = selectedCreative?.label || `Creative ${creativeId}`;

      if (isEditMode) {
        // In edit mode, just send the updated data back
        const updatedCreativeData = {
          creativeID: finalCreativeId,
          creativeName: finalCreativeName,
          file: selectedFile || undefined, // Convert null to undefined for TypeScript
          selectedMetrics,
          availableMetricsObjects, // Send complete available metrics objects
          customMetrics,
          outputLocation: outputLocation || editingCreative?.file,
          localFileUrl: localFileUrl || undefined, // Convert null to undefined
          fileType: fileType || editingCreative?.fileType,
        };

        onConfirm(updatedCreativeData);
      } else {
        // Create new creative object
        const newCreativeInput: CreativeInput = {
          id: `${sectionPosition}.${existingCreatives.length}`,
          position: existingCreatives.length + 1,
          creativeOutputLocation: outputLocation!,
          creativeID: finalCreativeId,
          creativeName: finalCreativeName,
          metrics: [
            // Add available metrics objects (already complete with column and decimals)
            ...availableMetricsObjects.map(
              (metricObj, index): CreativeMetricsInput => ({
                position: index + 1,
                label: metricObj.label,
                value: metricObj.value,
                column: metricObj.column,
                decimals: metricObj.decimals,
              }),
            ),
            // Add custom metrics
            ...customMetrics.map(
              (customMetric, index): CreativeMetricsInput => ({
                position: availableMetricsObjects.length + index + 1,
                label: customMetric.label,
                value: customMetric.value,
                column: undefined, // Custom metrics don't have column reference
                decimals: customMetric.decimals || 2, // Use custom decimals or default
              }),
            ),
          ],
        };

        // Prepare all creatives for this section (existing + new)
        const existingCreativeInputs: CreativeInput[] = existingCreatives.map(
          (creative: any): CreativeInput => ({
            id: creative.id,
            position: creative.position,
            creativeOutputLocation: creative.file, // Use existing file location
            creativeID: creative.creativeID,
            creativeName: creative.creativeName,
            metrics:
              creative.metrics?.map(
                (metric: any): CreativeMetricsInput => ({
                  position: metric.position,
                  label: metric.label,
                  value: metric.value,
                  column: metric.column, // Preserve existing column reference
                  decimals: metric.decimals ?? 2, // Use existing or default to 2
                }),
              ) || [],
          }),
        );



        const allCreatives: CreativeInput[] = [...existingCreativeInputs, newCreativeInput];

        // Call storeQueriesV2 to save entire section with all creatives
        const storeQueryVariables: StoreQueriesV2MutationVariables = {
          campaign_id: activeCampaign?.campaignId?.toString() || '',
          position: sectionPosition,
          query_id:
            creativeLibrarySections.find((section) => section.position === sectionPosition)?.dataSourceName || '',
          creatives: allCreatives,
        };

        try {
          const response = await saveQueries(storeQueryVariables);

          if (response === '200') {
            // Section saved successfully
          } else {
            throw new Error('Failed to save section - received non-200 response');
          }
        } catch (error) {
          throw new Error('Failed to save creative to backend');
        }

        const creativeDataToSend = {
          creativeID: finalCreativeId,
          creativeName: finalCreativeName,
          file: selectedFile,
          selectedMetrics,
          availableMetricsObjects, // Add this for complete metrics data
          customMetrics,
          outputLocation: outputLocation || undefined, // Convert null to undefined
          localFileUrl: localFileUrl || undefined, // Convert null to undefined
          fileType: selectedFile!.type, // Add MIME type for proper preview
        };

        onConfirm(creativeDataToSend);
      }

      // Reset form
      setCreativeId('');
      setSelectedCreativeIds([]);
      setIsMultiSelect(false);
      setCustomCreativeName('');
      setSelectedFile(null);
      setSelectedMetrics([]);
      setCustomMetrics([]);
    } catch (error) {
      alert('Error saving creative. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal>
        <HeadModul>
          <button type="button" className="x" onClick={onClose}>
            <IoMdClose className="closeIcon" />
          </button>
          <ModulContainer style={{ maxHeight: '80vh' }}>
            <h1>{isEditMode ? 'Edit Creative' : 'Add New Creative'}</h1>

            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <ReactLoading
                  type="spinningBubbles"
                  color={theme.publishButton || theme.primaryGray}
                  width={40}
                  height={40}
                />
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '2rem', width: '100%' }}>
                  {/* Step 1: Upload Creative */}
                  <MidRow
                    style={{
                      flex: '1',
                      borderRight: `1px solid ${theme.border}`,
                      minHeight: '300px',
                      padding: '0 1rem',
                    }}
                  >
                    <h2>{isEditMode ? '1. Change File (optional)' : '1. Upload Creative'}</h2>
                    <FileUploader handleChange={handleFileChange} name="file" types={fileTypes}>
                      <div
                        style={{
                          border: `1px dashed ${theme.border}`,
                          borderRadius: '4px',
                          padding: '1rem',
                          textAlign: 'center',
                          cursor: 'pointer',
                          backgroundColor: theme.placeholderBackground,
                          fontSize: '0.8rem',
                        }}
                      >
                        {selectedFile ? (
                          <div>
                            <div style={{ marginBottom: '0.25rem' }}>✓ New File Selected</div>
                            <div style={{ color: theme.primaryGray, fontSize: '0.7rem' }}>{selectedFile.name}</div>
                          </div>
                        ) : isEditMode && editingCreative ? (
                          <div>
                            <div style={{ marginBottom: '0.25rem' }}>📁 Current File</div>
                            <div style={{ color: theme.primaryGray, fontSize: '0.7rem' }}>
                              {editingCreative.creativeName || editingCreative.creativeID}
                            </div>
                            <div style={{ color: theme.primaryGray, fontSize: '0.6rem', marginTop: '0.5rem' }}>
                              Click to upload new file (optional)
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div style={{ marginBottom: '0.25rem' }}>Add File</div>
                            <div style={{ color: theme.primaryGray, fontSize: '0.7rem' }}>{fileTypes.join(', ')}</div>
                          </div>
                        )}
                      </div>
                    </FileUploader>

                    {/* File Preview */}
                    {selectedFile && (
                      <div
                        style={{
                          marginTop: '0.5rem',
                          border: `1px solid ${theme.border}`,
                          borderRadius: '4px',
                          padding: '0.25rem',
                          backgroundColor: theme.placeholderBackground,
                          maxHeight: '80px',
                          maxWidth: '120px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        {selectedFile.type.startsWith('image/') ? (
                          <img
                            src={URL.createObjectURL(selectedFile)}
                            alt="Preview"
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          />
                        ) : selectedFile.type.startsWith('video/') ? (
                          <video
                            src={URL.createObjectURL(selectedFile)}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            muted
                          />
                        ) : (
                          <span className="material-symbols-outlined" style={{ fontSize: 24, color: '#999' }}>
                            description
                          </span>
                        )}
                      </div>
                    )}
                  </MidRow>

                  {/* Step 2: Creative ID */}
                  <MidRow style={{ flex: '1', borderRight: `1px solid ${theme.border}`, padding: '0 1rem' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <h2>2. Select Creative ID</h2>
                      <button
                        type="button"
                        onClick={toggleSelectMode}
                        style={{
                          fontSize: '0.7rem',
                          // padding: '0.25rem 0.5rem',
                          backgroundColor: isMultiSelect ? theme.primaryGray : 'transparent',
                          color: isMultiSelect ? 'white' : theme.primaryGray,
                          boxShadow: isMultiSelect ? 'none' : 'inherit',
                          // border: `1px solid ${theme.border}`,
                          // borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        {isMultiSelect ? 'Multi' : 'Single'}
                      </button>
                    </div>

                    {isMultiSelect && (
                      <div
                        style={{
                          marginBottom: '0.5rem',
                          padding: '0.5rem',
                          backgroundColor: theme.placeholderBackground || '#f8f9fa',
                          borderRadius: '4px',
                          border: `1px solid ${theme.border}`,
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        <label
                          style={{
                            fontSize: '0.8rem',
                            color: theme.primaryGray,
                            display: 'block',
                            marginBottom: '0.25rem',
                            // fontWeight: 'bold',
                          }}
                        >
                          Custom Creative Name:
                        </label>
                        <input
                          type="text"
                          value={customCreativeName}
                          onChange={(e) => setCustomCreativeName(e.target.value)}
                          placeholder="Enter custom name..."
                          style={{
                            display: 'block',
                            flex: '1',
                          }}
                        />
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.25rem',
                        marginBottom: '0.5rem',
                        maxHeight: '200px',
                        overflowY: 'auto',
                      }}
                    >
                      {availableCreativeIds.map((creative) => {
                        const isSelected = isMultiSelect
                          ? selectedCreativeIds.includes(creative.id)
                          : creativeId === creative.id;

                        return (
                          <Selections
                            key={creative.id}
                            onClick={() =>
                              isMultiSelect
                                ? handleMultiSelectToggle(creative.id)
                                : handleSingleSelectToggle(creative.id)
                            }
                            style={{
                              backgroundColor: isSelected ? theme.primaryGray : 'transparent',
                              color: isSelected ? 'white' : theme.primaryGray,
                              cursor: 'pointer',
                              textAlign: 'left',
                              margin: '0',
                              width: '100%',
                              position: 'relative',
                            }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', padding: '0.25rem' }}>
                              <span style={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{creative.label}</span>
                              <span style={{ fontSize: '0.7rem', opacity: 0.8 }} title={creative.label}>
                                ID: {creative.id}
                              </span>
                            </div>
                            {isMultiSelect && isSelected && (
                              <span
                                style={{
                                  position: 'absolute',
                                  right: '0.5rem',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  fontSize: '0.8rem',
                                }}
                                className="material-symbols-outlined"
                              >
                                check
                              </span>
                            )}
                          </Selections>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: theme.primaryGray, textAlign: 'center' }}>
                      {isMultiSelect
                        ? `${selectedCreativeIds.length} selected${customCreativeName ? ` - "${customCreativeName}"` : ' (name required)'}`
                        : creativeId
                          ? '1 selected'
                          : '0 selected'}
                    </div>
                  </MidRow>

                  {/* Step 3: Select Metrics */}
                  <MidRow style={{ flex: '1', minHeight: '400px', padding: '0 1rem' }}>
                    <h2>3. Select up to 4 Metrics</h2>

                    {/* Selected Metrics (shown when metrics are selected) */}
                    {(selectedMetrics.length > 0 || customMetrics.length > 0) && (
                      <div style={{ marginBottom: '1rem' }}>
                        <h2>Selected Metrics ({selectedMetrics.length + customMetrics.length}/4):</h2>
                        <RightPane style={{ border: 'none', padding: 0 }}>
                          {/* Selected data metrics */}
                          <div className="listContainer">
                            {selectedMetrics.map((metricId) => {
                              const metric = availableMetrics.find((m) => m.id === metricId);
                              return (
                                <Selections
                                  key={metricId}
                                  chosen={true}
                                  style={{
                                    position: 'relative',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                  }}
                                >
                                  <span>{metric?.label || metricId}</span>
                                  <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                                    <span
                                      style={{
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        padding: '2px 4px',
                                        opacity: 0.7,
                                      }}
                                      onClick={() => handleEditDataMetric(metricId)}
                                      title="Edit metric"
                                      className="material-symbols-outlined"
                                    >
                                      edit
                                    </span>
                                    <span
                                      style={{
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        padding: '2px 4px',
                                        opacity: 0.7,
                                      }}
                                      onClick={() => handleMetricToggle(metricId)}
                                      title="Remove metric"
                                      className="material-symbols-outlined"
                                    >
                                      close
                                    </span>
                                  </div>
                                </Selections>
                              );
                            })}

                            {/* Selected custom metrics */}
                            {customMetrics.map((metric, index) => (
                              <Selections
                                key={index}
                                chosen={true}
                                style={{
                                  position: 'relative',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <span>
                                  {metric.label}: {metric.value}
                                </span>
                                <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                                  <span
                                    style={{
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      padding: '2px 4px',
                                      opacity: 0.7,
                                    }}
                                    onClick={() => handleEditCustomMetric(index)}
                                    title="Edit custom metric"
                                    className="material-symbols-outlined"
                                  >
                                    edit
                                  </span>
                                  <span
                                    style={{
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      padding: '2px 4px',
                                      opacity: 0.7,
                                    }}
                                    onClick={() => handleRemoveCustomMetric(index)}
                                    title="Remove custom metric"
                                    className="material-symbols-outlined"
                                  >
                                    close
                                  </span>
                                </div>
                              </Selections>
                            ))}
                          </div>
                        </RightPane>
                      </div>
                    )}

                    {/* Available Metrics from Data */}
                    <div style={{ marginBottom: '1rem' }}>
                      <h2>Available Metrics:</h2>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.25rem',
                          maxHeight: '150px',
                          overflowY: 'auto',
                        }}
                      >
                        {availableMetrics.map((metric) => (
                          <Selections
                            key={metric.id}
                            onClick={() => handleMetricToggle(metric.id)}
                            chosen={selectedMetrics.includes(metric.id)}
                            style={{
                              opacity: !selectedMetrics.includes(metric.id) && !canAddMoreMetrics() ? 0.5 : 1,
                              backgroundColor: selectedMetrics.includes(metric.id) ? theme.primaryGray : 'transparent',
                              color: selectedMetrics.includes(metric.id) ? 'white' : theme.primaryGray,
                              cursor:
                                !selectedMetrics.includes(metric.id) && !canAddMoreMetrics()
                                  ? 'not-allowed'
                                  : 'pointer',
                            }}
                          >
                            <span title={metric.label}>{metric.id}</span>
                            {/* {metric.description && (
                            <span className="questionMark tooltip">
                              ?<span className="tooltiptext">{metric.description}</span>
                            </span>
                          )} */}
                          </Selections>
                        ))}
                      </div>
                    </div>

                    {/* Custom Metrics */}
                    <RightPane style={{ border: 'none', padding: 0, marginBottom: '1rem' }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '0.5rem',
                        }}
                      >
                        <h2 style={{ margin: 0, fontSize: '0.9rem' }}>Custom Metrics:</h2>
                        {canAddMoreMetrics() && (
                          <Selections
                            onClick={() => setShowCustomModal(true)}
                            style={{
                              fontSize: '0.7rem',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: 'transparent',
                              color: theme.primaryGray,
                              cursor: 'pointer',
                              border: `1px solid ${theme.border}`,
                              textAlign: 'center',
                              margin: 0,
                            }}
                          >
                            + Add Custom
                          </Selections>
                        )}
                      </div>
                      <div className="listContainer">
                        {customMetrics.map((customMetric, index) => (
                          <Selections
                            key={index}
                            chosen={true}
                            style={{
                              position: 'relative',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <span>
                              {customMetric.label}: {customMetric.value}
                            </span>
                            <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                              <span
                                style={{
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  padding: '2px 4px',
                                  opacity: 0.7,
                                }}
                                onClick={() => handleEditCustomMetric(index)}
                                title="Edit custom metric"
                                className="material-symbols-outlined"
                              >
                                edit
                              </span>
                              <span
                                style={{
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  padding: '2px 4px',
                                  opacity: 0.7,
                                }}
                                onClick={() => handleRemoveCustomMetric(index)}
                                title="Remove custom metric"
                                className="material-symbols-outlined"
                              >
                                close
                              </span>
                            </div>
                          </Selections>
                        ))}
                      </div>
                    </RightPane>

                    {/* Summary */}
                    <div style={{ fontSize: '0.7rem', color: theme.primaryGray, textAlign: 'center' }}>
                      {getTotalMetricsCount()}/4 selected ({availableMetricsObjects.length} data +{' '}
                      {customMetrics.length} custom)
                    </div>
                  </MidRow>
                </div>

                {/* Actions */}
                <BottomRow style={{ marginTop: '2rem', borderTop: `1px solid ${theme.border}`, paddingTop: '1rem' }}>
                  <NavigationButton type="button" style={{ backgroundColor: 'transparent' }} onClick={onClose}>
                    Cancel
                  </NavigationButton>
                  <DarkButton
                    type="button"
                    onClick={handleFetch}
                    disabled={
                      (isMultiSelect ? selectedCreativeIds.length === 0 || !customCreativeName.trim() : !creativeId) ||
                      (!isEditMode && !selectedFile) ||
                      (availableMetricsObjects.length === 0 && customMetrics.length === 0)
                    }
                    style={{
                      opacity:
                        (isMultiSelect ? selectedCreativeIds.length > 0 && customCreativeName.trim() : creativeId) &&
                        (isEditMode || selectedFile) &&
                        (availableMetricsObjects.length > 0 || customMetrics.length > 0)
                          ? 1
                          : 0.5,
                    }}
                  >
                    {isEditMode ? 'Update' : 'Save'}
                  </DarkButton>
                </BottomRow>
              </>
            )}
          </ModulContainer>
        </HeadModul>
      </Modal>

      {/* Custom Metric Modal - Separate modal */}
      {showCustomModal && (
        <Modal>
          <HeadModul>
            <button
              type="button"
              className="x"
              onClick={() => {
                setShowCustomModal(false);
                setCustomLabel('');
                setCustomValue('');
              }}
            >
              <IoMdClose className="closeIcon" />
            </button>
            <ModulContainer style={{ maxWidth: '400px' }}>
              <h1>Add Custom Metric</h1>
              <div className="editDataForm">
                <h1>
                  Metric Label
                  <input
                    type="text"
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                    placeholder="e.g., Custom CTR"
                  />
                </h1>
                <h1>
                  Metric Value
                  <input
                    type="number"
                    step="0.01"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    placeholder="e.g., 2.45"
                  />
                </h1>
              </div>

              <div style={{ display: 'flex', marginTop: '10px', gap: '10px', marginLeft: 'auto', marginRight: '10px' }}>
                <IconButton
                  type="button"
                  onClick={handleAddCustomMetric}
                  disabled={!customLabel || !customValue}
                  style={{
                    opacity: customLabel && customValue ? 1 : 0.5,
                  }}
                >
                  Save
                </IconButton>
                <IconButton
                  type="button"
                  onClick={() => {
                    setShowCustomModal(false);
                    setCustomLabel('');
                    setCustomValue('');
                  }}
                >
                  Cancel
                </IconButton>
              </div>
            </ModulContainer>
          </HeadModul>
        </Modal>
      )}

      {/* Edit Metric Modal - Separate modal for editing existing metrics */}
      {showEditModal && editingMetric && (
        <Modal>
          <HeadModul>
            <button type="button" className="x" onClick={handleCancelEdit}>
              <IoMdClose className="closeIcon" />
            </button>
            <ModulContainer style={{ maxWidth: '400px' }}>
              <h1>Edit Metric</h1>
              <div className="editDataForm">
                <h1>
                  Metric Label
                  <input
                    type="text"
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    placeholder="e.g., Custom CTR"
                  />
                </h1>
                <h1>
                  Metric Value{editingMetric.type === 'data' ? ' *' : ''}
                  <input
                    type="number"
                    step="0.01"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    placeholder="e.g., 2.45"
                  />
                </h1>
                <h1>
                  Decimal Places
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="1"
                    value={editDecimals}
                    onChange={(e) => setEditDecimals(e.target.value)}
                    placeholder="e.g., 2"
                  />
                </h1>
              </div>

              {editingMetric.type === 'data' && (
                <small
                  style={{
                    fontSize: '0.75rem',
                    color: '#666',
                    display: 'block',
                    marginTop: '10px',
                    marginBottom: '10px',
                    fontStyle: 'italic',
                    textAlign: 'left',
                  }}
                >
                  * Changing this value will convert the metric to a custom metric and it will no longer update
                  automatically from data-lake
                </small>
              )}

              <div style={{ display: 'flex', marginTop: '10px', gap: '10px', marginLeft: 'auto', marginRight: '10px' }}>
                <IconButton
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={!editLabel || !editValue}
                  style={{
                    opacity: editLabel && editValue ? 1 : 0.5,
                  }}
                >
                  Save
                </IconButton>
                <IconButton type="button" onClick={handleCancelEdit}>
                  Cancel
                </IconButton>
              </div>
            </ModulContainer>
          </HeadModul>
        </Modal>
      )}
    </>
  );
};

export default AddCreativeModal;
